import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { Order } from '@gatecheckpro/contracts';
import { Button, Card, LoadingRegion, Notice } from '@gatecheckpro/design-system';
import { Chrome } from './Chrome.tsx';
import { useCommand } from '../lib/useCommand.ts';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * Where the payment provider sends the customer back to.
 *
 * This page deliberately reads **nothing** from the URL it was reached by. Not
 * a status parameter, not a transaction reference, not the fact that the
 * customer arrived at all. Every one of those is attacker-controlled: a person
 * who wants free tickets can type this address directly, and any page that
 * concluded "they came back, so they paid" would hand them out.
 *
 * What it does instead is ask the server, which asks the provider. The answer
 * on screen is whatever GateCheckPro independently established.
 *
 * The page exists purely so a returning customer gets that answer in seconds
 * rather than waiting for a webhook to be delivered and drained. Removing it
 * would make the product slower; trusting the redirect would make it wrong.
 */
export function PaymentReturnPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const check = useCommand<Order>();
  const [order, setOrder] = useState<Order | null>(null);
  const [attempts, setAttempts] = useState(0);

  const { run } = check;
  useEffect(() => {
    if (orderId === undefined) return;
    let cancelled = false;
    void run(`/api/v1/customer/orders/${orderId}/refresh-payment`, { method: 'POST' }).then(
      (result) => {
        if (!cancelled && result !== null) setOrder(result);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [orderId, run, attempts]);

  const paid =
    order !== null &&
    (order.status === 'PAID_PENDING_ISSUANCE' ||
      order.status === 'TICKETS_ISSUED' ||
      order.status === 'COMPLETED');
  const issued =
    order !== null && (order.status === 'TICKETS_ISSUED' || order.status === 'COMPLETED');

  return (
    <Chrome>
      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose" style={{ maxWidth: '40rem' }}>
          <div className="gc-page-header">
            <span className="gc-eyebrow">Payment</span>
            <h1>Checking with the provider</h1>
          </div>

          {check.busy && <LoadingRegion label="Confirming your payment" />}
          <FailureNotice error={check.error} />

          {order !== null && (
            <Card title={`Order ${order.orderNumber}`} eyebrow={order.event.title}>
              <div className="gc-stack">
                <Notice
                  tone={paid ? 'positive' : 'caution'}
                  title={
                    issued
                      ? 'Paid, and your tickets are ready'
                      : paid
                        ? 'Paid. Issuing your tickets'
                        : 'Not confirmed yet'
                  }
                >
                  {order.paymentMessage}
                </Notice>

                {issued && (
                  <Link to="/tickets" className="gc-button gc-button--primary">
                    See your tickets
                  </Link>
                )}
                {!paid && (
                  <div className="gc-stack gc-stack--tight">
                    <Button
                      busy={check.busy}
                      onClick={() => {
                        setAttempts((previous) => previous + 1);
                      }}
                    >
                      Check again
                    </Button>
                    <span className="gc-muted">
                      You can close this page. If the provider has taken your money, GateCheck
                      confirms it with them and your tickets appear on their own.
                    </span>
                  </div>
                )}
                <Link to={`/orders/${order.orderId}`}>Back to the order</Link>
              </div>
            </Card>
          )}
        </div>
      </main>
    </Chrome>
  );
}
